import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

export interface ColumnPickerOption {
  key: string;
  label: string;
}

/**
 * "Columns" button docked in a list page's search field, beside the filter
 * funnel: opens a checklist of the table's columns to show or hide. Replaces
 * the hide / add icons that used to appear on every column header.
 *
 * The page owns the visibility (and where it is saved); this only asks.
 */
@Component({
  selector: 'app-column-picker',
  templateUrl: './column-picker.component.html',
  styleUrls: ['./column-picker.component.scss']
})
export class ColumnPickerComponent {
  @Input() columns: ColumnPickerOption[] = [];
  /** Whether a column is currently shown. */
  @Input() isVisible: (key: string) => boolean = () => true;
  @Output() toggled = new EventEmitter<{ key: string; visible: boolean }>();

  open = false;

  constructor(private readonly host: ElementRef<HTMLElement>) { }

  get visibleCount(): number {
    return (this.columns || []).filter(c => this.isVisible(c.key)).length;
  }

  toggleOpen(event: MouseEvent): void {
    event.stopPropagation();
    this.open = !this.open;
  }

  onToggle(column: ColumnPickerOption, event: Event): void {
    const visible = (event.target as HTMLInputElement).checked;
    // Keep at least one column: an empty table can't be got back from.
    if (!visible && this.visibleCount <= 1) {
      (event.target as HTMLInputElement).checked = true;
      return;
    }
    this.toggled.emit({ key: column.key, visible });
  }

  trackByKey(_index: number, column: ColumnPickerOption): string {
    return column.key;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }
}
